import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import memoize from 'memoize-one';
import { BlockTabs, Button, ButtonGroup, Panel, TYPES, Notification } from 'sarsaparilla';

import SaveStatus from './SaveStatus';
import AssetTab from '../../components/StewardAssetEdit/AssetTab';
import ActivitiesTab from '../../components/StewardAssetEdit/ActivitiesTab';

import AddressesTab from '../../components/StewardAssetEdit/AddressesTab';
import LinkTab from '../../components/StewardAssetEdit/LinkTab';
import SiteMapTab from '../../components/StewardAssetEdit/SiteMapTab';
import EventsTab from '../../components/StewardAssetEdit/EventsTab';
import ImagesTab from '../../components/StewardAssetEdit/ImagesTab';


import { getState } from '../../reducers';
import { getAsset } from '../../reducers/asset';
import { getCurrentUser } from '../../reducers/currentUser';
import { getChangeCount, getErrorCount } from '../../reducers/changes';
import { getOrganization } from '../../reducers/organizations';

import { flattenOrg, orgsToOptions } from '../../utils/organization';
import { assetTypeDetails } from '../../utils/asset';

import * as saveStatusActions from '../../actions/saveStatus';
import * as assetActions from '../../actions/asset';
import * as changesActions from '../../actions/changes';
import * as organizationActions from '../../actions/organization';

import { STATE_READY, STATE_UNLOADED } from '../../../../ui-lottery/src/utils/async_utils';

const propTypes = {
    match: TYPES.MATCH
};

const StewardAssetEditPage = ({ match }) => {
    const [isProgressOpen, toggleProgressModal] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();

    const asset = useSelector((state) => getAsset(state));
    const organization = useSelector((state) => getOrganization(state, getCurrentUser(state).apiKey.orgId));
    const states = useSelector((state) => getState(state, 'organizations', 'currentUser', 'asset'))
    const changes = useSelector((state) => state.portal.changes)
    const changeCount = useSelector((state) => getChangeCount(state))
    const errorCount = useSelector((state) => getErrorCount(state))

    useEffect(() => {
        const { params: { assetId, assetType } } = match;

        if (states.organizations === STATE_UNLOADED) {
            dispatch(organizationActions.fetchOrganizations());
        }

        if (assetId) {
            const doesAssetNeedsUpdate = assetId && get(asset, 'id', '') !== assetId;
            if (doesAssetNeedsUpdate) {
                dispatch(assetActions.fetchAsset(assetType, assetId, { full: true }));
            }
        }

        // Clean Up
        return () => {
            dispatch(changesActions.removeRefs());
            dispatch(saveStatusActions.saveClearErrors());
        }
    }, []);

    const getOrgOptions = memoize((organizationsState, currentUserState) => {
        if (organizationsState === STATE_READY && currentUserState === STATE_READY) {
            if (organization) {
                return orgsToOptions(flattenOrg(organization));
            }
        }

        return [];
    });

    const handleCancel = () => {
        const { assetType } = match.params;
        history.push(`/data-steward/${assetType}/search`);
    }

    const handleSave = () => {
        const { params: { assetId } } = match;

        toggleProgressModal(true);

        dispatch(changesActions.saveChanges(changes, assetId))
            .then(() => {
                dispatch(assetActions.resetAsset());
            });
    }

    const handleCloseModal = ({ shouldRedirect }) => {
        const {
            params: { assetType, assetId }
        } = match;

        if (shouldRedirect) {
            history.push(`/data-steward/${assetType}/${assetId}/display`);
        }
        else {
            toggleProgressModal(false);
        }
    }

    const { params: { assetId, assetType } } = match;

    const {
        organizations: organizationsState,
        currentUser: currentUserState,
        asset: stateAsset
    } = states;

    // const tabClasses = getTabClasses(changes);
    const orgOptions = getOrgOptions(organizationsState, currentUserState);
    const details = assetTypeDetails(assetType);
    const loading = stateAsset !== STATE_READY || assetId !== asset.id;
    const reservable = get(asset, 'reservable', false);

    return (
        <div className='steward-asset-edit'>
            { reservable && (
                <Notification title="Warning:" type="warning">
                    This {details.name} is not editable.
                </Notification>
            )}

            { isProgressOpen && (
                <SaveStatus onClose={handleCloseModal} />
            )}
            
            <BlockTabs indentTabs>
                <Panel label={details.name} className="asset-tab">
                    <AssetTab
                        assetDetails={details}
                        value={asset}
                        orgOptions={orgOptions}
                        isLoading={loading} />
                </Panel>

                <Panel label="Activities" className="activities-tab">
                    <ActivitiesTab
                        activityType={details.activities}
                        value={asset}
                        isLoading={loading} />
                </Panel>

                <Panel label="Addresses" className='addresses-tab'>
                    <AddressesTab
                        addressType={details.addresses}
                        value={asset}
                        isLoading={loading} />
                </Panel>

                <Panel label="Links" className='links-tab'>
                    <LinkTab
                        linkType={details.links}
                        value={asset}
                        isLoading={loading} />
                </Panel>

                <Panel label="Images" className="images-tab">
                    <ImagesTab
                        mediaType={details.media}
                        value={asset}
                        isLoading={loading} />
                </Panel>

                <Panel label="Events" className='events-tab'>
                    <EventsTab
                        eventType={details.event}
                        assetType={assetType}
                        eventsToLoad={asset.eventsToLoad}
                        asset={asset}
                        isLoading={loading} />
                </Panel>

                <Panel label="Site Maps" className='siteMaps-tab'>
                    <SiteMapTab isLoading={loading} />
                </Panel>
            </BlockTabs>

            <div className="form-footer">
                <div className="footer-status">
                    STATUS: {changeCount} Changes, {errorCount} Errors
                </div>
                
                <ButtonGroup>
                    <Button appearance="tertiary" onClick={handleCancel}>Cancel</Button>
                    { !reservable &&
                        <Button onClick={handleSave} isLoading={loading}>Save Changes</Button>
                    }
                </ButtonGroup>
            </div>
        </div>
    );
}

StewardAssetEditPage.propTypes = propTypes;

export default StewardAssetEditPage;
